import { DocumentTextRegular } from "@fluentui/react-icons";
import classNames from "classnames";
import React, { useCallback, useEffect, useMemo } from "react";
import { Accept, useDropzone } from "react-dropzone";

import styles from "./index.module.css";

import { translation } from "@/i18n";
import { nsUpload } from "@/i18n/locales/upload";
import { generateId } from "@/utils/generateId";

const useTranslation = translation.use(nsUpload);

export type DragAndDropAcceptFileType =
  | "excel"
  | "csv"
  | "json"
  | "yaml"
  | "zip";

export const dragAndDropAcceptFiles: {
  [acceptFileType in DragAndDropAcceptFileType]: Accept;
} = {
  excel: {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      ".xlsx",
    ],
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.ms-excel.sheet.macroenabled.12": [".xlsm"],
  },
  csv: {
    "text/csv": [".csv"],
  },
  json: {
    "application/json": [".json"],
  },
  yaml: { "application/yaml": [".yml", ".yaml"] },
  zip: { "application/zip": [".zip"] },
};

export type DragAndDropUploadData = {
  file: File;
  id: string;
  isDisableDelete: boolean;
};

export type FileTypeConfig = {
  type: DragAndDropAcceptFileType;
  limit: number;
};

export const getFileType = (
  file: File
): DragAndDropAcceptFileType | undefined => {
  return (
    Object.keys(dragAndDropAcceptFiles) as DragAndDropAcceptFileType[]
  ).find((type) => {
    const exts = Object.values(dragAndDropAcceptFiles[type]).flat();
    return exts.some((ext) => file.name.toLowerCase().endsWith(ext));
  });
};

/**
 * @description ドラッグ&ドロップでデータをアップする部分を描画
 * アップロードされたデータのstateは外部から注入する前提
 */
export const DragAndDropFields: React.FC<{
  accept: Accept;
  uploadedFileList: DragAndDropUploadData[];
  setUploadedFileList: React.Dispatch<
    React.SetStateAction<DragAndDropUploadData[]>
  >;
  existingType: DragAndDropAcceptFileType | undefined;
  setExistingType: React.Dispatch<
    React.SetStateAction<DragAndDropAcceptFileType | undefined>
  >;
  fileTypeConfigs: FileTypeConfig[];
  extElement: React.ReactNode;
  isDisableDragAndDrop?: boolean;
}> = ({
  accept,
  uploadedFileList,
  setUploadedFileList,
  existingType,
  setExistingType,
  fileTypeConfigs,
  extElement,
  isDisableDragAndDrop = false,
}) => {
  const { t } = useTranslation();
  useEffect(() => {
    if (uploadedFileList.length === 0) {
      setExistingType(undefined);
      return;
    }
    setExistingType(getFileType(uploadedFileList[0].file));
  }, [uploadedFileList, setExistingType]);

  const currentLimit = useMemo(() => {
    if (!existingType) return 1;
    const cfg = fileTypeConfigs.find((c) => c.type === existingType);
    return cfg ? cfg.limit : 1;
  }, [existingType, fileTypeConfigs]);

  // 既存のアップロードデータがある場合は、そのデータのタイプに合わせてacceptを変更
  const dynamicAccept: Accept = useMemo(() => {
    if (!existingType) {
      return accept;
    }
    return dragAndDropAcceptFiles[existingType] ?? {};
  }, [existingType, accept]);

  const disableDragAndDrop =
    uploadedFileList.length >= currentLimit || isDisableDragAndDrop;

  const getLimitForType = (
    t: DragAndDropAcceptFileType | undefined,
    configs: FileTypeConfig[]
  ) => configs.find((c) => c.type === t)?.limit ?? 1;

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      // 1) 今回のドロップで採用するタイプを決める
      if (acceptedFiles.length === 0) return;
      const dropType = existingType ?? getFileType(acceptedFiles[0]); // 最初のファイルの拡張子から判定

      if (!dropType) return;

      // 2) タイプに応じた上限を取得
      const limitForDropType = getLimitForType(dropType, fileTypeConfigs);

      setUploadedFileList((prev) => {
        const nowFilesNum = prev.length;

        const slotsLeft = Math.max(0, limitForDropType - nowFilesNum);

        // 3) 今回ドロップ分は「決めたタイプ」と一致するものだけ受け入れ、上限まで追加
        const filesToAdd = acceptedFiles
          .filter((f) => getFileType(f) === dropType)
          .slice(0, slotsLeft)
          .map((file) => ({
            file,
            id: generateId(),
            isDisableDelete: false,
          }));
        return [...prev, ...filesToAdd];
      });
    },
    [setUploadedFileList, fileTypeConfigs, existingType]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: dynamicAccept,
    disabled: disableDragAndDrop,
  });

  return (
    <div className={styles.dragAndDrop}>
      {
        <div
          className={classNames(styles.file, {
            [styles.drag]: isDragActive,
            [styles.disable]: disableDragAndDrop,
          })}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <div className={styles.selectFile}>
            <DocumentTextRegular />
            {t.upload.dragAndDrop.selectFile()}
          </div>
          {t.upload.dragAndDrop.prompt()}
          <div>
            {t.upload.dragAndDrop.limitNote({
              fileLimits: fileTypeConfigs
                .map(({ type, limit }) =>
                  t.upload.dragAndDrop.limitItem({
                    typeName: t.upload.fileType[type](),
                    limit,
                    hasMultipleTypes: fileTypeConfigs.length > 1,
                  })
                )
                .join(t.upload.dragAndDrop.limitSeparator()),
            })}
          </div>
        </div>
      }
      {extElement}
    </div>
  );
};
